/** Non-rendering helpers for the run-parameter form. */
import type { InputSlot, RunParam } from "./api";
import { LS_PARAM_PREFIX } from "./config";
import { getCurrentCustomerId } from "./customer";

/** the sticky values of one customer never prefill another's form */
function stickyKey(name: string): string {
  const customer = getCurrentCustomerId();
  return `${LS_PARAM_PREFIX}${customer ? `${customer}.` : ""}${name}`;
}

/** Month names in the given locale, capitalized ("janvier" → "Janvier"). */
export function monthNames(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale, { month: "long" });
  return Array.from({ length: 12 }, (_, i) => {
    const name = format.format(new Date(2000, i, 1));
    return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  });
}

/** Short month names in the given locale ("juin", "juil." — never both "Jui").
 *
 * Slicing the long name to three letters collided in French: juin and juillet
 * both rendered "Jui", so a chart and a closing calendar showed the same label
 * for two months. Intl knows each locale's own abbreviation. */
export function monthShortNames(locale: string): string[] {
  const format = new Intl.DateTimeFormat(locale, { month: "short" });
  return Array.from({ length: 12 }, (_, i) => {
    const name = format.format(new Date(2000, i, 1));
    return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
  });
}

export function loadSticky(name: string): string {
  try {
    return window.localStorage.getItem(stickyKey(name)) ?? "";
  } catch {
    return "";
  }
}

export function saveStickyParams(params: RunParam[], values: Record<string, string>) {
  for (const p of params) {
    // a secret is never remembered, whatever the tool declares
    if (p.secret || p.control === "password") continue;
    if (p.sticky && (values[p.name] ?? "").trim()) {
      try {
        window.localStorage.setItem(stickyKey(p.name), values[p.name]);
      } catch {
        /* private mode */
      }
    }
  }
}

/** A run parameter's effective value: what was typed, else its declared
 *  default — the value the tool will actually run with. */
export function effectiveValue(
  params: RunParam[],
  values: Record<string, string>,
  name: string,
): string {
  const typed = (values[name] ?? "").trim();
  if (typed) return typed;
  return params.find((p) => p.name === name)?.default ?? "";
}

/**
 * Does a run with these parameter values need an uploaded file? Mirrors
 * ToolSpec.input_required on the server: the first input slot's "required"
 * (default true), or "required_when" evaluated on effective values.
 */
export function inputRequired(
  tool: { input_slots?: InputSlot[]; params: RunParam[] },
  values: Record<string, string>,
): boolean {
  const slot = tool.input_slots?.[0];
  if (!slot) return true;
  const condition = slot.required_when;
  if (!condition || Object.keys(condition).length === 0) return slot.required !== false;
  return Object.entries(condition).every(
    ([name, value]) =>
      effectiveValue(tool.params, values, name).toUpperCase() === value.toUpperCase(),
  );
}

/**
 * Is this parameter shown for the current values? Hidden-audience params
 * never are; `visible_when` is evaluated on EFFECTIVE values, so a field tied
 * to the default choice shows before anything is typed.
 */
export function isVisible(
  params: RunParam[],
  values: Record<string, string>,
  p: RunParam,
): boolean {
  if (p.audience === "hidden") return false;
  if (!p.visible_when) return true;
  return Object.entries(p.visible_when).every(
    ([name, value]) => effectiveValue(params, values, name) === value,
  );
}

/** The parameter a page renders as tabs above the upload zone, if any. */
export function tabsParam(params: RunParam[]): RunParam | null {
  return params.find((p) => p.control === "tabs" && p.audience !== "hidden") ?? null;
}

/**
 * Required operator parameters still empty — among the VISIBLE ones only (a
 * credential behind the other tab is not missing), counting a declared
 * default as filled (the form shows it selected).
 */
export function missingRequired(
  params: RunParam[],
  values: Record<string, string>,
  requireAll: boolean,
): RunParam[] {
  return params.filter(
    (p) =>
      p.audience === "operator" &&
      (p.required || requireAll) &&
      isVisible(params, values, p) &&
      !effectiveValue(params, values, p.name),
  );
}
